import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import {
  CustomPagination, RowTable, TitleWithActions,
} from 'components/components';
import VentureBuilderWallet from 'components/VentureBuilderWallet';
import { ManagedStartupType, ManagedStartupWalletType } from 'packages/venture_builder_repository/types';
import { usePortalPortfolio, usePortalScope } from 'packages/venture_builder_repository/portal/hooks';
import { parseNumberToPrice } from 'utils/helpers/priceFormatters';
import NOT_SPECIFIED from 'utils/constants/display';
import routes from 'routes';
import {
  EmptyPortfolio, LoadError, Loading, Refresh,
} from './shared';
import '../Dashboard/Dashboard.scss';
import './VentureBuilderPortal.scss';

const walletValue = (startup: ManagedStartupType, key: keyof ManagedStartupWalletType) => (
  startup.wallet ? parseNumberToPrice(startup.wallet[key], 'AED') : NOT_SPECIFIED
);

const columns: ColumnsType<ManagedStartupType> = [
  { key: 'name', title: 'Startup', dataIndex: 'name' },
  {
    key: 'email', title: 'Contact email', dataIndex: 'email', className: 'vb-portal-dashboard__email',
  },
  { key: 'balance', title: 'Balance', render: (_, startup) => walletValue(startup, 'balance') },
  { key: 'total', title: 'Total', render: (_, startup) => walletValue(startup, 'total') },
  { key: 'consumed', title: 'Consumed', render: (_, startup) => walletValue(startup, 'consumed') },
  { key: 'blocked', title: 'Blocked', render: (_, startup) => walletValue(startup, 'blocked') },
  {
    key: 'actions',
    render: (_, startup) => (
      <Link className="ant-btn secondary-button" to={`${routes.servicesUrl}?startup=${startup.id}`}>
        Request service
      </Link>
    ),
  },
];

const VentureBuilderDashboard = () => {
  const portfolio = usePortalPortfolio(usePortalScope());
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const startups = portfolio.data;

  const page = Math.max(1, Math.min(currentPage, Math.ceil((startups?.length || 0) / limit)));

  return (
    <div className="content-container vb-portal vb-portal-dashboard">
      <div className="dashboard">
        <TitleWithActions title="Dashboard" subtitle="View your startups and wallet balances." size="l">
          <Refresh onClick={() => { portfolio.refetch(); }} loading={portfolio.isFetching} />
        </TitleWithActions>
        {portfolio.isPending && <Loading />}
        {portfolio.error && <LoadError error={portfolio.error} retry={() => { portfolio.refetch(); }} />}
        {!portfolio.error && startups && (
          <div className="dashboard-content">
            {!startups.length ? <EmptyPortfolio /> : (
              <>
                <VentureBuilderWallet startups={startups} />
                <section aria-label="Your startups">
                  <TitleWithActions title="Your startups" />
                  <div className="table">
                    <RowTable
                      rowKey="id"
                      columns={columns}
                      dataSource={startups.slice((page - 1) * limit, page * limit)}
                      isLoading={portfolio.isFetching}
                      scroll={{ x: true }}
                    />
                    {startups.length > 10 && (
                      <CustomPagination
                        limit={limit}
                        currentPage={page}
                        total={startups.length}
                        setCurrentPage={setCurrentPage}
                        setLimit={(value) => { setLimit(value); setCurrentPage(1); }}
                      />
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VentureBuilderDashboard;
